import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiSearch, FiShoppingCart, FiUser, FiMapPin, FiLogOut, FiLoader } from 'react-icons/fi';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import CategoryCard from '@/components/cards/CategoryCard';
import ProductCard from '@/components/cards/ProductCard';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Logo from '@/components/common/Logo';

const PRODUCTS_PER_PAGE = 12;

const ClienteHome = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [location, setLocation] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { user, logout } = useAuth();
  const { addToCart, getTotalItems } = useCart();
  const navigate = useNavigate();
  const observerRef = useRef();

  useEffect(() => {
    loadCategories();
    getLocation();
  }, []);

  useEffect(() => {
    // Reset pagination when category or search changes
    setPage(1);
    setProducts([]);
    setHasMore(true);
    loadProducts(1, true);
  }, [selectedCategory, location]);

  // Infinite scroll observer
  const lastProductRef = useCallback(node => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        loadMoreProducts();
      }
    }, { threshold: 0.5 });
    
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, loading]);

  const loadMoreProducts = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadProducts(nextPage, false);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Fallback para Barueri
          setLocation({ lat: -23.5114, lng: -46.8763 });
        }
      );
    } else {
      setLocation({ lat: -23.5114, lng: -46.8763 });
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProducts = async (pageNum = 1, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = {
        page: pageNum,
        limit: PRODUCTS_PER_PAGE
      };
      if (selectedCategory) params.category = selectedCategory.id;
      if (searchQuery) params.search = searchQuery;
      if (location) {
        params.lat = location.lat;
        params.lng = location.lng;
        params.radius_km = 50;
      }

      const response = await api.get('/products', { params });
      const newProducts = response.data;
      
      if (reset) {
        setProducts(newProducts);
      } else {
        setProducts(prev => [...prev, ...newProducts]);
      }
      
      // Check if there are more products
      setHasMore(newProducts.length === PRODUCTS_PER_PAGE);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setProducts([]);
    setHasMore(true);
    loadProducts(1, true);
  };

  const handleAddToCart = (product) => {
    addToCart(product.store_id, product);
  };

  const handleProductClick = (product) => {
    navigate(`/cliente/produto/${product.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header - BarueriFlash Brand */}
      <header className="bg-gradient-to-r from-[#1A1A1A] to-[#DC143C] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            {/* Logo */}
            <div className="flex items-center">
              <span className="font-bold text-sm md:text-lg text-white">BARUERI</span>
              <svg width="24" height="30" viewBox="0 0 80 100" className="md:w-7 md:h-9" style={{filter: 'drop-shadow(0 0 2px rgba(255, 215, 0, 0.5))'}}>
                <defs>
                  <linearGradient id="boltGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FFED4A" />
                    <stop offset="50%" stopColor="#FFD700" />
                    <stop offset="100%" stopColor="#FFA500" />
                  </linearGradient>
                </defs>
                <path d="M45 0 L25 40 L40 40 L20 70 L35 70 L10 100 L50 60 L35 60 L55 30 L40 30 L60 0 Z" fill="url(#boltGrad1)"/>
              </svg>
              <span className="font-bold text-sm md:text-lg text-[#FFD700]">FLASH</span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-1 md:gap-3">
              <button
                onClick={() => navigate('/cliente/carrinho')}
                className="relative p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Carrinho"
              >
                <FiShoppingCart className="text-lg md:text-xl" />
                {getTotalItems() > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-white text-[#DC143C] text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center">
                    {getTotalItems()}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate('/cliente/pedidos')}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Meus Pedidos"
              >
                <FiUser className="text-lg md:text-xl" />
              </button>
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Sair"
                data-testid="cliente-logout-btn"
              >
                <FiLogOut className="text-lg md:text-xl" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative">
            <FiSearch className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar em Barueri..."
              className="w-full pl-10 md:pl-12 pr-4 py-2.5 md:py-3 rounded-xl text-gray-800 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#DC143C]"
            />
          </form>

          {location && (
            <div className="flex items-center gap-1 mt-2 text-white/70 text-sm">
              <FiMapPin className="text-xs" />
              <span>📍 Barueri, SP</span>
            </div>
          )}
        </div>
      </header>

      {/* Categories */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">Categorias</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`flex-shrink-0 px-4 py-2 rounded-full transition-all font-medium ${
                !selectedCategory
                  ? 'bg-[#DC143C] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            {categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                selected={selectedCategory?.id === category.id}
                onClick={setSelectedCategory}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[#1A1A1A]">
            {selectedCategory ? selectedCategory.name : 'Todos os Produtos'}
          </h2>
          <span className="text-sm text-gray-500">{products.length} produtos</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Carregando produtos..." />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 animate-fadeIn">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <FiSearch className="text-4xl text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Nenhum produto encontrado</h3>
            <p className="text-gray-500">Tente buscar por outro termo ou categoria</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((product, index) => {
                const isLast = index === products.length - 1;
                return (
                  <div 
                    key={product.id}
                    ref={isLast ? lastProductRef : null}
                    className="animate-slideUp"
                    style={{ animationDelay: `${(index % PRODUCTS_PER_PAGE) * 50}ms` }}
                  >
                    <ProductCard
                      product={product}
                      onAddToCart={handleAddToCart}
                      onClick={() => handleProductClick(product)}
                    />
                  </div>
                );
              })}
            </div>
            
            {/* Loading more indicator */}
            {loadingMore && (
              <div className="flex justify-center py-8">
                <div className="flex items-center gap-2 text-gray-500">
                  <FiLoader className="animate-spin" />
                  <span>Carregando mais produtos...</span>
                </div>
              </div>
            )}
            
            {/* End of list */}
            {!hasMore && products.length > 0 && (
              <div className="text-center py-8 text-gray-400">
                <p>Você viu todos os {products.length} produtos</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm">© 2025 BarueriFlash - Entregas rápido como um raio! ⚡</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/faq" className="text-white/60 hover:text-white transition-colors">FAQ</Link>
              <Link to="/sobre" className="text-white/60 hover:text-white transition-colors">Sobre</Link>
              <Link to="/termos" className="text-white/60 hover:text-white transition-colors">Termos</Link>
              <Link to="/privacidade" className="text-white/60 hover:text-white transition-colors">Privacidade</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClienteHome;
